import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formatNumber, i18nIntl } from '../../lib/ep.js';

import ActionButton, { SquaredButton } from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import Message, { AlertMessage } from '../builder/Message.js';
import ProgressBar from '../builder/ProgressBar.js';
import Spinner from '../builder/Spinner.js';
import Tooltip from '../builder/Tooltip';

import { ProfileModule } from '../ui_items/Module.js';
import PackSelectionLayer from './PackSelectionLayer.js';

export const PurchaseLayer = (props) => {
	return (
		<PackSelectionLayer
			title="SMS Package purchase"
			pack_type={1}
			avail_units={[100, 500, 2500, 5000, 10000, 25000, 50000]}
			unit_label={{
				singular:"SMS",
				plural:"SMS",
			}}
			onPurchase={(qty) => {
				setData((current) => {
					current.total += qty;
					return { ...current };
				});
				setDisplayPurchaseLayer(false);
			}}
			conditions={[
				"SMS purchased and not consumed do not expire.",
				"The SMS accumulate in your account and you can use them for any of your promotions.",
				"Due to carrier restrictions phone login is only available in certain countries, please contact support to check if your country could be enabled.",
			]}
			{...props}
		/>
	)
};

const Wrapper = (props) => {
	const is_builder = props?.is_builder ?? false;
	const add_module = props?.add_module ?? false;

	if(is_builder && !add_module) {
		return (
			<div
				class={`gui_pos_rel ${props?.classes ?? ''}`}
				style={props.style}
			>{props.children}</div>
		)
	}

	return (
		<ProfileModule
			title={{
				label:"Login by phone number",
				icon:'knk-package',
			}}
			is_gapped={false}
			style={props.style}
			_class={{ other:props.classes }}
		>{props.children}</ProfileModule>
	)
};

const SMSPacksStatus = (props) => {
	const i18n = (m) => i18nIntl(m, props.intl);

	const [ display_purchase_layer, setDisplayPurchaseLayer ] = useState(false);
	const [ data, setData ] = useState({ total:100, used:1, initial:true });
	const [ is_processing, setIsProcessing ] = useState(false);

	const account_id = props.account_id ?? "";
	const is_builder = props?.is_builder ?? false;
	const add_module = props?.add_module ?? false;

	const updateCredit = () => {
		$.get(`/accounts/get_sms_credit/${account_id}`, (res) => {
			setData(res);
		}, 'JSON');
	};

	const executeDelayedAction = (cb) => {
		setIsProcessing(true);

		setTimeout(() => {
			cb();
			setIsProcessing(false);
		}, (Math.round(Math.random() * 1000) + 1000));
	};

	useEffect(() => {
		updateCredit();
	}, []);

	const warning_percent = 25;
	const total = parseInt(data.total);
	const pending = total - parseInt(data.used);
	const percent = formatNumber(100 * pending / total, 1);
	const almost_finished = (percent <= warning_percent) ? true : false;
	const still_available = (pending > 0) ? true : false;

	let classes = ['ep_pack_status'];
	if(!is_builder || !add_module) classes.push('not_in_top');
	if(!still_available) classes.push('is_finished');
	if(almost_finished) classes.push('almost_finished');

	const CTA_component = (!is_builder) ? ActionButton : ActionLink;

	if(is_processing) {
		const block_h = '100px';

		return (
			<Wrapper
				classes={classes.join(" ")}
				is_builder={is_builder}
				add_module={add_module}
				style={{
					height:block_h,
					outline:'1px dashed rgba(var(--main-text-color), 0.15)',
					outlineOffset:'0',
				}}
			>
				<Spinner
					style={{ height:block_h }}
				/>
			</Wrapper>
		);
	}
	
	return (
		<Wrapper
			classes={classes.join(" ")}
			is_builder={is_builder}
			add_module={add_module}
		>
			<Message
				_class={{ other:(is_builder && add_module) ? 'in_bottom' : 'in_top' }}
			>
				<p>
					{i18n("To verify the participants' phone number, it is necessary to send a code by SMS.")}

					<ActionLink
						_href={`${window.zendesk_base_url}13791817238292`}
						_target='_blank'
						_class={{ other:'gui_not_in_start_tiny' }}
					>More information</ActionLink>
				</p>
			</Message>

			<div class={`pack_progress${(is_builder) ? ' gui_not_in_top_small' : ''}`}>
				{(data?.initial ?? false) ? (
					<></>
				) : (
					<FormattedMessage
						id="SMS available in your account: {pending} out of {total} ({percent}%)"
						values={{
							pending:<strong>{pending}</strong>,
							total:<strong>{total}</strong>,
							percent,
						}}
					/>
				)}

				{(still_available) && (
					<ProgressBar
						actual={pending}
						max={total}
						display_percent={false}
						display_percent_in_bar={false}
						size="big"
						reinforcement_color='var(--ep-red-color)'
						animation_duration={`${0.05 * (100 - percent)}s`}
						extra_contents={<SquaredButton
							icon='knk-reload'
							onClick={() => executeDelayedAction(() => updateCredit())}
							tooltip={<Tooltip
								with_icon={false}
								position='top'
							>Refresh</Tooltip>}
							_class={{ other:'reload_bar' }}
						/>}
					/>
				)}
			</div>

			{(!still_available) && (
				<AlertMessage
					title="Login by phone disabled"
					_class={{ other:'in_top in_bottom'}}
				/>
			)}

			<ButtonCollection
				_class={{ other:'gui_flex_start' }}
				style={{
					gap:'1em',
				}}
			>
				<span>
					[ <CTA_component
						secondary={(almost_finished) ? false : true}
						onClick={() => setDisplayPurchaseLayer(true)}
					>Purchase a SMS package</CTA_component> ]
				</span>
			</ButtonCollection>

			{(display_purchase_layer) && (
				<PurchaseLayer
					onPurchase={(qty) => {
						setData((current) => {
							current.total += qty;
							return { ...current };
						});
						setDisplayPurchaseLayer(false);
					}}
					close={() => setDisplayPurchaseLayer(false)}
				/>
			)}
		</Wrapper>
	);
}

export default injectIntl(SMSPacksStatus);